import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthUser } from '@/hooks/useAuthUser';
import type { UserDoc, WithId } from '@/types/firestore';
import { Loader2, ShieldAlert } from 'lucide-react';
import { FORCE_ADMIN_MODE } from '@/config/devOverrides';

interface RequireSuperAdminProps {
  children: React.ReactNode;
}

/**
 * RequireSuperAdmin Guard
 * 
 * Restricts access to super admin users only.
 * Support admins are redirected to /admin dashboard.
 * 
 * Usage:
 *   <RequireSuperAdmin>
 *     <AdminSettingsPage />
 *   </RequireSuperAdmin>
 */
export default function RequireSuperAdmin({ children }: RequireSuperAdminProps) {
  const { status, firebaseUser, userDoc, loading: authLoading } = useAuthUser();
  const [claimsState, setClaimsState] = useState<{ loading: boolean; role: 'super' | 'support' | null }>({
    loading: false,
    role: null,
  });
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (!firebaseUser) {
      setClaimsState({ loading: false, role: null });
      setHasTimedOut(false);
      return;
    }

    let active = true;
    setHasTimedOut(false);
    setClaimsState({ loading: true, role: null });

    const timeoutId = window.setTimeout(() => {
      if (active) {
        console.error('[RequireSuperAdmin] Token refresh timed out after 5s');
        setHasTimedOut(true);
        setClaimsState({ loading: false, role: null });
      }
    }, 5_000);

    firebaseUser
      .getIdTokenResult(true)
      .then((result) => {
        if (!active) {
          return;
        }

        const claims = result.claims ?? {};
        const roleClaim = typeof claims.role === 'string' ? claims.role : undefined;
        
        // Determine role: Only 'super' or 'support' are valid roles
        // Do NOT treat admin:true as super admin - must have explicit role:'super'
        const normalizedRole = roleClaim === 'super' ? 'super' : roleClaim === 'support' ? 'support' : null;

        if (import.meta.env.DEV) {
          console.info('[RequireSuperAdmin] Super admin claim check:', {
            email: firebaseUser.email,
            role: normalizedRole,
            roleClaimRaw: roleClaim,
            claims,
          });
        }

        setClaimsState({ loading: false, role: normalizedRole });
        window.clearTimeout(timeoutId);
      })
      .catch((error) => {
        console.error('Error checking super admin claim:', error);
        if (active) {
          setClaimsState({ loading: false, role: null });
        }
      })
      .finally(() => {
        if (!active) {
          return;
        }
        window.clearTimeout(timeoutId);
      });

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [firebaseUser]);

  const roleFromDoc = useMemo(() => {
    const doc = userDoc as WithId<UserDoc> | null;
    if (!doc) {
      return null;
    }

    const roles = doc.roles ?? {};
    const flags = roles as Record<string, unknown>;
    const hasFlag = (key: string) => flags[key] === true;

    const flagRole = typeof flags['role'] === 'string'
      ? (flags['role'] as string).toLowerCase()
      : undefined;

    if (doc.isAdmin === true || hasFlag('isAdmin') || hasFlag('isSuperAdmin') || hasFlag('admin') || hasFlag('super') || flagRole === 'super') {
      return 'super' as const;
    }

    if (hasFlag('isSupportAdmin') || hasFlag('support') || flagRole === 'support') {
      return 'support' as const;
    }

    return null;
  }, [userDoc]);

  const isAuthenticated = status === 'authed' && !!firebaseUser;
  const combinedLoading = authLoading || claimsState.loading || status === 'loading';
  
  // Determine final role (prefer claims over doc)
  const finalRole = claimsState.role || roleFromDoc;
  const isSuperAdmin = finalRole === 'super' || FORCE_ADMIN_MODE;

  // Still loading/initializing or checking admin status
  if (combinedLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="text-center">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-orange-500" />
          <p className="mt-4 text-slate-400">Verifying super admin access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Not a super admin, timed out, or is support admin - show denial UI
  if (!isSuperAdmin || hasTimedOut || finalRole === 'support') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4" data-testid="access-denied">
        <div className="max-w-md rounded-lg border-2 border-red-500/30 bg-red-900/20 p-8 text-center">
          <ShieldAlert className="mx-auto h-16 w-16 text-red-400" />
          <h1 className="mt-4 text-2xl font-bold text-red-200" data-testid="access-denied-title">Super Admin Access Required</h1>
          <p className="mt-2 text-red-200/80" data-testid="access-denied-message">
            {hasTimedOut 
              ? "Super admin verification timed out. Please try again or contact support."
              : finalRole === 'support'
                ? "This page requires super admin privileges. Support admins do not have access to this feature."
                : "This page requires super admin privileges."
            }
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <button
              onClick={() => window.history.back()}
              className="rounded-lg bg-slate-700 px-6 py-2 font-medium text-slate-200 transition-colors hover:bg-slate-600"
            >
              Go Back
            </button>
            <button
              onClick={() => window.location.href = '/admin'}
              className="rounded-lg bg-orange-600 px-6 py-2 font-medium text-white transition-colors hover:bg-orange-700"
            >
              Admin Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // User is super admin
  return <>{children}</>;
}
